import React, { useEffect, useRef, useState } from 'react';
import FreeCamToggle from './FreeCamToggle';
import MuteToggle from './MuteToggle';

interface InfoOverlayProps {
    visible: boolean;
}

const NAME_TEXT = 'Sujal Kishore';
const TITLE_TEXT = 'Software Engineer';
const MULTIPLIER = 1;

const InfoOverlay: React.FC<InfoOverlayProps> = ({ visible }) => {
    const visRef = useRef(visible);
    const [nameText, setNameText] = useState('');
    const [titleText, setTitleText] = useState('');
    const [time, setTime] = useState(new Date().toLocaleTimeString());
    const timeRef = useRef(time);
    const [timeText, setTimeText] = useState('');
    const [textDone, setTextDone] = useState(false);
    const [controlsVisible, setControlsVisible] = useState(false);

    const typeText = (
        i: number,
        curText: string,
        text: string,
        setText: React.Dispatch<React.SetStateAction<string>>,
        callback: () => void,
        refOverride?: React.MutableRefObject<string>
    ) => {
        if (refOverride) text = refOverride.current;
        if (i < text.length) {
            setTimeout(() => {
                if (visRef.current === true)
                    window.postMessage({ type: 'keydown', key: `_AUTO_${text[i]}` }, '*');
                setText(curText + text[i]);
                typeText(i + 1, curText + text[i], text, setText, callback, refOverride);
            }, Math.random() * 50 + 50 * MULTIPLIER);
        } else {
            callback();
        }
    };

    useEffect(() => {
        if (visible && nameText === '') {
            setTimeout(() => {
                typeText(0, '', NAME_TEXT, setNameText, () => {
                    typeText(0, '', TITLE_TEXT, setTitleText, () => {
                        typeText(0, '', time, setTimeText, () => {
                            setTextDone(true);
                        }, timeRef);
                    });
                });
            }, 400);
        }
        visRef.current = visible;
    }, [visible]);

    useEffect(() => {
        if (textDone) {
            setTimeout(() => setControlsVisible(true), 300);
        }
    }, [textDone]);

    useEffect(() => {
        window.postMessage({ type: 'keydown', key: `_AUTO_` }, '*');
    }, [controlsVisible]);

    useEffect(() => {
        const interval = setInterval(() => {
            setTime(new Date().toLocaleTimeString());
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        timeRef.current = time;
        if (textDone) setTimeText(time);
    }, [time]);

    return (
        <div style={styles.wrapper}>
            {nameText !== '' && (
                <div className="macos-glass-card" style={styles.nameCard}>
                    <p style={styles.nameText}>{nameText}</p>
                    {titleText !== '' && (
                        <p style={styles.titleText}>{titleText}</p>
                    )}
                </div>
            )}
            {timeText !== '' && (
                <div style={styles.bottomRow}>
                    <div className="macos-glass-card" style={styles.timeCard}>
                        <p style={styles.timeText}>{timeText}</p>
                    </div>
                    {controlsVisible && (
                        <div style={styles.controlsRow}>
                            <MuteToggle />
                            <FreeCamToggle />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    wrapper: {
        position: 'absolute',
        top: 0,
        left: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: 24,
        gap: 4,
        width: 'auto',
    },
    nameCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        minWidth: 160,
    },
    nameText: {
        fontSize: 15,
        fontWeight: 600,
        color: 'rgba(255,255,255,0.95)',
        letterSpacing: -0.3,
    },
    titleText: {
        fontSize: 12,
        fontWeight: 400,
        color: 'rgba(255,255,255,0.55)',
        letterSpacing: 0,
    },
    bottomRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginTop: 4,
    },
    timeCard: {
        padding: '8px 14px',
        display: 'flex',
        alignItems: 'center',
    },
    timeText: {
        fontSize: 13,
        fontWeight: 400,
        color: 'rgba(255,255,255,0.7)',
        letterSpacing: 0.3,
        fontVariantNumeric: 'tabular-nums',
    },
    controlsRow: {
        display: 'flex',
        flexDirection: 'row',
        gap: 8,
    },
};

export default InfoOverlay;
